import {
    Accordion,
    AccordionButton,
    AccordionIcon,
    AccordionItem,
    AccordionPanel,
    Box,
    ButtonProps,
    Divider,
    Drawer,
    DrawerContent,
    Flex,
    IconButton,
    Image,
    Tooltip,
    useDisclosure,
    useTheme,
} from '@chakra-ui/react';
import { AiOutlineFile } from 'react-icons/ai';
import CanopyTooltip from '../../canopy-ui-components/components/CanopyTooltip/CanopyTooltip';
import CanopyIconButton from '../../canopy-ui-components/components/IconButton/IconButton';
import { useCurrentColors } from '../../canopy-ui-components/hooks/UseCurrentColors/UseCurrentColors';
import ViewDocuments from '../Upload/documents-storage/ticketView/ViewDocuments';
import ViewTicket from './ViewTicket';

const TicketDetails: Record<string, Record<string, string>> = {
    ViewSecurity: {
        tooltip: 'ui.mm.tooltip.view-security',
        headerText: 'security_name',
        subHeaderText: 'ticker',
    },
    ViewTransactions: {
        tooltip: 'ui.mm.tooltip.view-ticket',
        headerText: 'Ticket',
        subHeaderText: 'ticketref',
    },
};
export interface ViewTicketDetailsProps extends ButtonProps {
    rowsHeader?: any;
    globalProps?: GlobalProps;
    data?: any;
    columns?: PropTableColumn[];
    refreshData?: (args?: any) => void;
    userId?: number;
    tagValue?: string;
    ticketType?: 'ViewSecurity' | 'ViewTransactions';
}

export const ViewTicketDetails: any = ({
    rowsHeader,
    globalProps = { t: () => {}, redirectTo: () => {}, qp: {} },
    data,
    columns,
    refreshData = () => {},
    userId,
    tagValue,
    ticketType = 'ViewSecurity',
}: ViewTicketDetailsProps) => {
    const currentColors = useCurrentColors();
    const { isOpen, onOpen, onClose } = useDisclosure();
    const { t } = globalProps || {};
    const theme = useTheme();
    return (
        <>
            <Tooltip
                aria-label={ticketType}
                label={t(TicketDetails[ticketType]?.tooltip)}
                placement="bottom-start"
                hasArrow>
                <IconButton
                    background="transparent"
                    cursor="pointer"
                    border="none"
                    aria-label={ticketType}
                    width="14px"
                    minWidth="14px"
                    height="14px"
                    onClick={onOpen}
                    icon={<AiOutlineFile />}
                    backgroundColor="transparent"
                    color={currentColors.brand['300']}
                    variant="ghost"
                    _hover={{ backgroundColor: 'transparent' }}
                    _focus={{
                        border: '0px',
                        backgroundColor: 'transparent',
                        outline: 'none',
                    }}
                    _active={{ border: '0px' }}
                    mr="4px"
                />
            </Tooltip>
            <Drawer
                isOpen={isOpen}
                placement="right"
                onClose={onClose}
                size="sm">
                <DrawerContent
                    px="17px"
                    py="30px"
                    height={[
                        'calc(100% - 10px)',
                        'calc(100% - 10px)',
                        'calc(100% - 10px)',
                        '100vh',
                        '100vh',
                    ]}
                    backgroundColor={currentColors.componentBg}>
                    <Flex maxHeight="200px">
                        <Image
                            src={theme?.images?.headerLogo}
                            maxWidth="100%"
                            width="auto"
                            height={'35px'}
                            mr="17.5px"
                        />
                        <Flex
                            direction="column"
                            width="calc(100% - 35px)">
                            <CanopyTooltip
                                aria-label={
                                    data[
                                        TicketDetails[ticketType]['headerText']
                                    ] || TicketDetails[ticketType]['headerText']
                                }
                                label={
                                    data[
                                        TicketDetails[ticketType]['headerText']
                                    ] || TicketDetails[ticketType]['headerText']
                                }
                                width="100%"
                                noOfLines={2}
                                fontSize="14px"
                                fontWeight="bold"
                                display="flex"
                                alignItems="center">
                                {data?.[
                                    TicketDetails?.[ticketType]?.['headerText']
                                ] || TicketDetails[ticketType]['headerText']}
                            </CanopyTooltip>
                            <CanopyTooltip
                                aria-label={
                                    data[
                                        TicketDetails[ticketType][
                                            'subHeaderText'
                                        ]
                                    ]
                                }
                                label={
                                    data[
                                        TicketDetails[ticketType][
                                            'subHeaderText'
                                        ]
                                    ]
                                }
                                width="100%"
                                color="whiteAlpha.500"
                                fontSize="14px"
                                fontWeight="medium"
                                display="flex"
                                alignItems="center">
                                {
                                    data[
                                        TicketDetails[ticketType][
                                            'subHeaderText'
                                        ]
                                    ]
                                }
                            </CanopyTooltip>
                        </Flex>
                        <CanopyIconButton
                            background="transparent"
                            cursor="pointer"
                            border="none"
                            color={currentColors.brand['500']}
                            _hover={{ backgroundColor: 'transparent' }}
                            width="20px"
                            height="20px"
                            aria-label="remove"
                            icon={'ActClose'}
                            variant="ghost"
                            onClick={onClose}
                        />
                    </Flex>

                    <Divider
                        borderColor={currentColors.brand['700']}
                        width="100%"
                        my="10px"
                    />

                    <Flex
                        width="100%"
                        overflow="auto"
                        direction="column">
                        <Accordion
                            allowToggle
                            width="100%"
                            allowMultiple
                            defaultIndex={[0, 1]}>
                            {rowsHeader.map((row: string, index: number) => {
                                return (
                                    <AccordionItem
                                        key={row}
                                        border={0}
                                        mb="15px">
                                        <AccordionButton
                                            cursor="pointer"
                                            height="30px"
                                            p="8px"
                                            _focus={{
                                                border: '0px',
                                                outline: 'none',
                                            }}
                                            _active={{ border: '0px' }}>
                                            <Box
                                                flex="1"
                                                textAlign="left"
                                                color={
                                                    currentColors.brand['10']
                                                }>
                                                {t(rowsHeader?.[index])}
                                            </Box>
                                            <AccordionIcon />
                                        </AccordionButton>
                                        <AccordionPanel p={4}>
                                            {row === 'view-details' ? (
                                                <ViewTicket
                                                    globalProps={globalProps}
                                                    data={data}
                                                    refreshData={refreshData}
                                                    columns={
                                                        columns
                                                    }></ViewTicket>
                                            ) : null}
                                            {row === 'view-documents' ? (
                                                <ViewDocuments
                                                    tagValue={tagValue}
                                                    globalProps={globalProps}
                                                    refreshData={refreshData}
                                                    userId={
                                                        userId
                                                    }></ViewDocuments>
                                            ) : null}
                                        </AccordionPanel>
                                    </AccordionItem>
                                );
                            })}
                        </Accordion>
                    </Flex>
                </DrawerContent>
            </Drawer>
        </>
    );
};
